import { z } from 'zod/v3';

// ===== RFC-104: Policy Types =====

export const PolicyTypeSchema = z.enum([
  'INFORMATION_SECURITY',
  'ACCESS_CONTROL',
  'DATA_MANAGEMENT',
  'RISK_MANAGEMENT',
  'INCIDENT_RESPONSE',
  'BUSINESS_CONTINUITY',
  'SECURE_DEVELOPMENT',
  'OPERATIONS_SECURITY',
  'PHYSICAL_SECURITY',
  'CRYPTOGRAPHY',
  'CODE_OF_CONDUCT',
  'ACCEPTABLE_USE',
  'VENDOR_MANAGEMENT',
  'PRIVACY',
  'OTHER',
]);

export type PolicyType = z.infer<typeof PolicyTypeSchema>;

// ===== Policy Execution Config (stored in GuidedTemplate.executionConfig) =====

export const PolicyExecutionConfigSchema = z.object({
  /** Policy type for categorization */
  policyType: PolicyTypeSchema,
  /** Whether employees must acknowledge this policy */
  requiresAcknowledgment: z.boolean().default(false),
  /** Due date for acknowledgments (ISO date string) */
  acknowledgmentDueDate: z.string().optional(),
  /** When the policy became active (ISO date string) */
  effectiveDate: z.string().optional(),
});

export type PolicyExecutionConfig = z.infer<typeof PolicyExecutionConfigSchema>;

// ===== Policy Acknowledgment DTOs =====

export const PolicyAcknowledgmentDTOSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  userId: z.string(),
  version: z.number(),
  acknowledgedAt: z.date(),
  ipAddress: z.string().nullable(),
  // Optional nested user info for display
  user: z.object({
    id: z.string(),
    name: z.string().nullable(),
    email: z.string(),
    image: z.string().nullable(),
  }).optional(),
});

export type PolicyAcknowledgmentDTO = z.infer<typeof PolicyAcknowledgmentDTOSchema>;

export const CreatePolicyAcknowledgmentDTOSchema = z.object({
  templateId: z.string(),
  version: z.number(),
  ipAddress: z.string().optional(),
});

export type CreatePolicyAcknowledgmentDTO = z.infer<typeof CreatePolicyAcknowledgmentDTOSchema>;

// ===== Policy Summary (for list views) =====

export const PolicySummaryDTOSchema = z.object({
  templateId: z.string(),
  name: z.string(),
  policyType: PolicyTypeSchema,
  currentVersion: z.number(),
  effectiveDate: z.string().nullable(),
  verifiedAt: z.date().nullable(),
  requiresAcknowledgment: z.boolean(),
  acknowledgmentDueDate: z.string().nullable(),
  totalUsers: z.number(),
  acknowledgedCount: z.number(),
  pendingCount: z.number(),
});

export type PolicySummaryDTO = z.infer<typeof PolicySummaryDTOSchema>;
